import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { skipToken } from '@reduxjs/toolkit/query'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { isNeuropsychEvaluationProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  CareTeamMember,
  CareTeamProgramCardProps,
} from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/careTeamProgramCard/CareTeamProgramCard'

import { useCareTeamProviderName } from './useCareTeamProviderName'
import { useCareTeamSlotLabel } from './useCareTeamSlotLabel'
import { getAuthUserId } from '../../../../../data/auth/authSelectors'
import { useGetCareTeamSlotsQuery } from '../../../../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../../../../episodes/data/hooks/useSelectedEpisodeForClient'

export type CareTeamData = Pick<CareTeamProgramCardProps, 'members' | 'unassigned'>

export const useCareTeamData = ({ skip }: { skip?: boolean } = {}): CareTeamData | undefined => {
  const getProviderName = useCareTeamProviderName()
  const getSlotLabel = useCareTeamSlotLabel()
  const providerId = useSelector(getAuthUserId)
  const { isNPTestingEnabled } = useFlags()

  const { data: selectedEpisode } = useSelectedEpisodeForClient()
  const shouldFetch = !skip && isNPTestingEnabled && isNeuropsychEvaluationProgram(selectedEpisode?.program_name)

  const { currentData: careTeam } = useGetCareTeamSlotsQuery(
    shouldFetch && selectedEpisode ? { episodeId: selectedEpisode.id } : skipToken,
  )

  return useMemo(() => {
    if (!shouldFetch || !careTeam) {
      return undefined
    }

    const members: CareTeamMember[] = careTeam.slots.flatMap((slot) =>
      slot.assignments.map((assignment) => {
        const displayName = getProviderName(assignment)
        return {
          providerId: assignment.provider_lyra_id,
          displayName,
          role: getSlotLabel(slot),
          isSelf: assignment.provider_lyra_id === providerId,
          avatarDetails: { src: assignment.photo_url, displayName },
        }
      }),
    )

    const unassignedRoles = careTeam.slots.filter((slot) => slot.assignments.length === 0).map(getSlotLabel)

    if (members.length === 0 && unassignedRoles.length === 0) {
      return undefined
    }

    return {
      members,
      unassigned: unassignedRoles.length ? { roles: unassignedRoles } : undefined,
    }
  }, [shouldFetch, careTeam, providerId, getProviderName, getSlotLabel])
}
